import { useEffect, useRef, useState } from "react";
import "./workflow.css";

interface WorkflowStep {
  num: string;
  title: string;
  body: string;
  tags: string[];
}

const STEPS: WorkflowStep[] = [
  {
    num: "01",
    title: "01\nIntake",
    body: "Market & goals input",
    tags: [
      "Business Goals",
      "Audience",
      "Competitors",
      "Pain Points",
      "Market Research",
    ],
  },
  {
    num: "02",
    title: "02\nBrief",
    body: "Strategy & intent lock",
    tags: [
      "Brand Strategy",
      "Positioning",
      "Messaging",
      "Tone of Voice",
      "Creative Brief",
    ],
  },
  {
    num: "03",
    title: "03\nIdentity",
    body: "Voice & Visuals generated",
    tags: [
      "Logo",
      "Typography",
      "Color Palette",
      "Visual System",
      "Brand Assets",
    ],
  },
  {
    num: "04",
    title: "04\nLaunch",
    body: "Execution ready output",
    tags: ["Website", "Marketing", "Social Media", "Pitch Deck", "Launch Kit"],
  },
];

export default function WorkflowSection() {
  const sectionRef = useRef<HTMLElement | null>(null);

  const [progress, setProgress] = useState(0);

  const TOTAL_PHASES = 7;

  useEffect(() => {
    let animationFrame: number;

    const handleScroll = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(() => {
        const section = sectionRef.current;

        if (!section) return;

        const rect = section.getBoundingClientRect();

        const startPoint = window.innerHeight * 0.6; // start when heading reaches this point

        const endPoint = -rect.height + window.innerHeight * 0.6;

        const distance = startPoint - endPoint;

        const current = startPoint - rect.top;

        let value = current / distance;

        value = Math.min(Math.max(value, 0), 1);

        // smooth interpolation
        setProgress((prev) => prev + (value - prev) * 0.08);
      });
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);

      cancelAnimationFrame(animationFrame);
    };
  }, []);
  const phase = progress * TOTAL_PHASES;
  const cardVisible = [phase >= 0.7, phase >= 2.3, phase >= 3.8, phase >= 5];
  return (
    <section className="wf-section" ref={sectionRef}>
      <div className="wf-header">
        <span className="wf-subtitle">Engine</span>

        <h2 className="wf-title">The AIGIO Engine</h2>
      </div>

      <div className="wf-wrapper">
        {/* Timeline */}

        <div className="wf-line">
          <div className="wf-line-track" />

          <div
            className="wf-line-progress"
            style={{
              height: `${Math.min(phase / 6, 1) * 100}%`,
            }}
          />
        </div>

        {STEPS.map((step, index) => {
          const isReverse = index % 2 === 1;

          return (
            <div
              key={step.num}
              className={`wf-row ${cardVisible[index] ? "expanded" : ""}`}
            >
              {!isReverse ? (
                <>
                  {/* =========================
                      LEFT CONTENT
                  ========================= */}

                  <div className="wf-content-side">
                    <div className="wf-content-card">
                      <h3>
                        {step.title.split("\n").map((line, i) => (
                          <span key={i}>
                            {line}
                            <br />
                          </span>
                        ))}
                      </h3>

                      <p>{step.body}</p>
                    </div>
                  </div>

                  {/* =========================
                      CENTER TIMELINE
                  ========================= */}

                  <div className="wf-center">
                    <div
                      className={`wf-dot ${cardVisible[index] ? "active" : ""}`}
                    />
                  </div>

                  {/* =========================
                      RIGHT TAGS
                  ========================= */}

                  <div className="wf-tag-side">
                    <div className="wf-tag-card">
                      {step.tags.map((tag) => (
                        <span key={tag} className="wf-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* =========================
                      LEFT TAGS
                  ========================= */}

                  <div className="wf-tag-side">
                    <div className="wf-tag-card reverse">
                      {step.tags.map((tag) => (
                        <span key={tag} className="wf-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* =========================
                      CENTER TIMELINE
                  ========================= */}

                  <div className="wf-center">
                    <div
                      className={`wf-dot ${cardVisible[index] ? "active" : ""}`}
                    />
                  </div>

                  {/* =========================
                      RIGHT CONTENT
                  ========================= */}

                  <div className="wf-content-side">
                    <div className="wf-content-card reverse">
                      <h3>
                        {step.title.split("\n").map((line, i) => (
                          <span key={i}>
                            {line}
                            <br />
                          </span>
                        ))}
                      </h3>

                      <p>{step.body}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
